import { Box, Button } from '@chakra-ui/react';
import type { MouseEvent } from 'react';
import { memo, useState } from 'react';

import { useTrialUnblur } from './TrialUnblurProvider/TrialUnblurProvider';

export interface BlurredCellProps {
  value: string;
  encryptedValue?: string;
  revealedValue?: string;
  userKey?: string;
  fieldKey?: string;
  onMouseEnter: () => void;
  allowUnblur?: boolean;
  isCompact?: boolean;
  'data-testid'?: string;
}

export const BlurredCell = memo(function BlurredCell({
  value,
  encryptedValue,
  revealedValue,
  userKey,
  fieldKey,
  onMouseEnter,
  allowUnblur,
  isCompact = true,
  'data-testid': testId,
}: BlurredCellProps) {
  const trialUnblur = useTrialUnblur();
  const [isHovered, setIsHovered] = useState(false);
  const hasEncryptedValue =
    typeof encryptedValue === 'string' && encryptedValue.trim().length > 0;
  const decryptedValue =
    userKey && fieldKey && trialUnblur
      ? trialUnblur.getDecryptedValue({ userKey, fieldKey })
      : undefined;
  const resolvedRevealedValue = revealedValue ?? decryptedValue;
  const isRevealed = Boolean(resolvedRevealedValue);
  const isDecrypting =
    userKey && fieldKey && trialUnblur
      ? trialUnblur.isDecrypting({ userKey, fieldKey })
      : false;
  const shouldShowUnblurButton = Boolean(
    allowUnblur && !isRevealed && hasEncryptedValue && isHovered
  );

  const handleUnblur = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const canRequestDecrypt = Boolean(
      allowUnblur && hasEncryptedValue && trialUnblur && userKey && fieldKey
    );
    if (!canRequestDecrypt || !trialUnblur || !userKey || !fieldKey) return;
    void trialUnblur.requestDecrypt({
      userKey,
      fieldKey,
      encryptedValue: encryptedValue ?? '',
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    onMouseEnter();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <Box
      position="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={testId}
    >
      {isRevealed ? (
        <Box position="relative">{resolvedRevealedValue}</Box>
      ) : (
        <Box
          filter="blur(3px)"
          position="relative"
          borderRadius={isCompact ? '100%' : undefined}
          maxWidth={isCompact ? '20px' : undefined}
          data-testid="blurred-cell"
          pointerEvents="none"
          cursor="default"
        >
          {value}
        </Box>
      )}

      {shouldShowUnblurButton && (
        <Button
          size="xs"
          onClick={handleUnblur}
          onMouseDown={(event) => event.stopPropagation()}
          position="absolute"
          top="0px"
          bottom="0px"
          left="0px"
          right="0px"
          margin="auto"
          width="60px"
          height="15px"
          colorScheme="yellow"
          background="#fff7c9"
          color="navyBlue.500"
          _hover={{ background: 'yellow.300' }}
          isLoading={isDecrypting}
        >
          Unblur
        </Button>
      )}
    </Box>
  );
});

export default BlurredCell;
